/**
 * Subscriptions Block - Alpine.js Component
 *
 * Handles subscription/one-time purchase selection for product pages.
 * Integrates with Shopify's selling plans API.
 */

var subscriptionsBlockComponent = () => ({
  sectionId: null,
  validVariantIds: [],
  selectedType: null,
  selectedSellingPlan: null,
  selectedPlanData: null, // Store the full plan object for direct access
  sellingPlans: [],
  variantPrice: 0,
  variantComparePrice: 0,
  subscribeCompareBasis: 'onetime', // 'onetime' or 'compare_at'
  quantity: 1,
  priceTemplates: {},
  offerDiscountType: null,
  offerDiscountValue: 0,
  offerDiscountScope: 'total',
  selectionSideEffectsTimer: null,
  perUnitSavings: 0, // Per-unit savings amount in cents
  perUnitSavingsRounded: 0, // Per-unit savings rounded to nearest dollar (in cents)
  lockedByOffer: false, // True while a selected offer dictates the plan + hides the frequency picker

  init() {
    this.initializeData();
    this.initializeSellingPlan();
    this.updateFormWithRetry();
    this.dispatchSubscriptionEvent();
    this.attachEventListeners();

    // Update price display if quantity > 1 from initial offer or in checkbox mode
    if (this.quantity > 1 || this.isCheckboxMode()) {
      this.updatePriceDisplay();
    }

    // Always update savings amount display (handles quantity-based [amount] placeholders)
    this.updateSavingsAmountDisplay();

    // Watch selectedType changes for checkbox mode - ensures price updates on toggle
    if (this.isCheckboxMode()) {
      this.$watch('selectedType', () => {
        this.updateCheckboxPriceDisplay();
      });
    }

    // Watch for selling plan changes to ensure container prices update
    // This is critical for when the delivery frequency changes
    this.$watch('selectedSellingPlan', () => {
      this.$nextTick(() => {
        this.updatePriceDisplay();
        this.updateFrequencyDisplay();
      });
    });

    // Retry checking for selected offer in case offers block initializes later
    this.retryInitFromOffer();
  },

  retryInitFromOffer(attempts = 0) {
    const maxAttempts = 5;
    const retryDelay = 50;

    if (attempts >= maxAttempts) return;

    setTimeout(() => {
      const section = this.$el.closest('.shopify-section');
      if (!section) return;

      const selectedOffer = section.querySelector('.offer.is-selected');
      if (selectedOffer) {
        const offerQuantity = parseInt(selectedOffer.dataset.quantity, 10) || 1;
        const currentOfferDiscountType = selectedOffer.dataset.offerDiscountType || null;
        const currentOfferDiscountValue = parseFloat(selectedOffer.dataset.offerDiscountValue) || 0;

        // Only update if values changed from what we had
        if (offerQuantity !== this.quantity ||
            currentOfferDiscountType !== this.offerDiscountType ||
            currentOfferDiscountValue !== this.offerDiscountValue) {
          this.quantity = offerQuantity;
          this.offerDiscountType = currentOfferDiscountType;
          this.offerDiscountValue = currentOfferDiscountValue;
          this.offerDiscountScope = selectedOffer.dataset.offerDiscountScope || 'total';
          this.updatePriceDisplay();
          this.dispatchSubscriptionEvent();
        }

        // Apply a plan lock from the preselected offer (covers the offers block
        // initializing before our event listener was attached).
        const lockedPlanRaw = selectedOffer.dataset.lockedSellingPlanId || '';
        const lockedPlanId = lockedPlanRaw ? parseInt(lockedPlanRaw, 10) : null;
        if (Number.isFinite(lockedPlanId) && lockedPlanId > 0) {
          if (!this.lockedByOffer || Number(this.selectedSellingPlan) !== lockedPlanId) {
            this.lockToPlan(lockedPlanId);
          }
        }
      } else {
        // No selected offer found yet, retry
        this.retryInitFromOffer(attempts + 1);
      }
    }, retryDelay);
  },

  initializeData() {
    const preselectedOption = (this.$el.getAttribute('data-preselected-option') || '').trim();
    const sellingPlansJson = this.$el.getAttribute('data-selling-plans');
    const variantPriceAttr = this.$el.getAttribute('data-variant-price');
    const variantComparePriceAttr = this.$el.getAttribute('data-variant-compare-price');
    const sectionFromDom = this.$el.closest('.shopify-section')?.id?.replace('shopify-section-', '') || null;
    const variantIdsJson = this.$el.getAttribute('data-variant-ids');

    this.sectionId = this.$el.getAttribute('data-section-id') || sectionFromDom;
    try {
      const variantIds = JSON.parse(variantIdsJson || '[]');
      this.validVariantIds = Array.isArray(variantIds) ? variantIds.map(String) : [];
    } catch (e) {
      this.validVariantIds = [];
    }

    try {
      this.sellingPlans = JSON.parse(sellingPlansJson || '[]');
    } catch (e) {
      this.sellingPlans = [];
      console.warn('Subscriptions block: invalid selling plans JSON payload', e);
    }
    this.variantPrice = parseInt(variantPriceAttr, 10) || 0;
    this.variantComparePrice = parseInt(variantComparePriceAttr, 10) || 0;
    this.subscribeCompareBasis = this.$el.getAttribute('data-subscribe-compare-basis') || 'onetime';
    this.perUnitSavings = parseInt(this.$el.getAttribute('data-per-unit-savings'), 10) || 0;
    this.perUnitSavingsRounded = parseInt(this.$el.getAttribute('data-per-unit-savings-rounded'), 10) || 0;

    this.priceTemplates = {
      onetimePrice: this.$el.getAttribute('data-onetime-price-template') || '<strong>[price]</strong>',
      onetimeCompare: this.$el.getAttribute('data-onetime-compare-template') || '[compare_price]',
      subscribePrice: this.$el.getAttribute('data-subscribe-price-template') || '<strong>[price]</strong>',
      subscribeCompare: this.$el.getAttribute('data-subscribe-compare-template') || '[compare_price]'
    };

    const initialType = preselectedOption === 'onetime' ? 'onetime' : 'subscribe';
    this.selectedType = initialType;

    // Read initial quantity and offer discount from any preselected offer
    this.initializeFromSelectedOffer();

    this.$nextTick(() => {
      this.selectedType = initialType;
    });
  },

  initializeFromSelectedOffer() {
    // Find any selected offer in the same section
    const section = this.$el.closest('.shopify-section');
    if (!section) return;

    const selectedOffer = section.querySelector('.offer.is-selected');
    if (selectedOffer) {
      // Read quantity from the selected offer
      const offerQuantity = parseInt(selectedOffer.dataset.quantity, 10) || 1;
      if (offerQuantity > 1) {
        this.quantity = offerQuantity;
      }

      // Read offer discount data
      this.offerDiscountType = selectedOffer.dataset.offerDiscountType || null;
      this.offerDiscountValue = parseFloat(selectedOffer.dataset.offerDiscountValue) || 0;
      this.offerDiscountScope = selectedOffer.dataset.offerDiscountScope || 'total';
    } else {
      // No selected offer yet - check form quantity input as fallback
      const qtyInput = section.querySelector('[name="quantity"]');
      if (qtyInput) {
        const inputQuantity = parseInt(qtyInput.value, 10) || 1;
        if (inputQuantity > 1) {
          this.quantity = inputQuantity;
        }
      }
    }
  },

  initializeSellingPlan() {
    if (this.sellingPlans.length > 0) {
      this.selectedSellingPlan = this.sellingPlans[0].id;
      this.selectedPlanData = this.sellingPlans[0];
    }
  },

  attachEventListeners() {
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));
    document.addEventListener('offers:quantity-changed', this.handleQuantityChange.bind(this));
    document.addEventListener('offers:prices-updated', this.handleOfferPricesUpdated.bind(this));
    document.addEventListener('offers:lock-selling-plan', this.handleSellingPlanLock.bind(this));

    const section = this.$el.closest('.shopify-section');
    if (section) {
      const quantityInput = section.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.addEventListener('change', (e) => {
          this.quantity = parseInt(e.target.value, 10) || 1;
          this.updatePriceDisplay();
          this.dispatchSubscriptionEvent();
        });
      }
    }
  },

  handleVariantChange(event) {
    const eventSectionId = event.detail?.sectionId;
    if (eventSectionId && this.sectionId && String(eventSectionId) !== String(this.sectionId)) {
      return;
    }

    if (event.detail?.variant) {
      const eventVariantId = String(event.detail.variant.id);
      if (this.validVariantIds.length > 0 && !this.validVariantIds.includes(eventVariantId)) {
        return;
      }

      this.variantPrice = event.detail.variant.price;
      this.variantComparePrice = event.detail.variant.compare_at_price || 0;
      this.updatePriceDisplay();
      this.dispatchSubscriptionEvent();
    }
  },

  handleSellingPlanLock(event) {
    const eventSectionId = event.detail?.sectionId;
    if (eventSectionId && this.sectionId && String(eventSectionId) !== String(this.sectionId)) {
      return;
    }

    const planId = parseInt(event.detail?.sellingPlanId, 10);
    if (Number.isFinite(planId) && planId > 0) {
      this.lockToPlan(planId);
    } else {
      this.releasePlanLock();
    }
  },

  // Force subscribe + select a specific plan, then hide the frequency picker.
  lockToPlan(planId) {
    const numericId = parseInt(planId, 10);
    if (!Number.isFinite(numericId) || numericId <= 0) return;
    // Ignore plans this block doesn't actually offer (e.g. mismatched product).
    if (this.sellingPlans.length > 0 && !this.sellingPlans.some((p) => Number(p.id) === numericId)) {
      return;
    }
    this.lockedByOffer = true;
    // this.selectedType = 'subscribe';
    this.selectSellingPlan(numericId);
  },

  // Release the lock; restore normal frequency selection (leaves the current plan as-is).
  releasePlanLock() {
    if (this.lockedByOffer) {
      this.lockedByOffer = false;
    }
  },

  handleQuantityChange(event) {
    const eventSectionId = event.detail?.sectionId;
    if (eventSectionId && this.sectionId && String(eventSectionId) !== String(this.sectionId)) {
      return;
    }

    if (event.detail && event.detail.quantity) {
      const section = this.$el.closest('.shopify-section');
      const scopedOffer = event.detail.blockId
        ? section?.querySelector(`.offer[data-block-id="${event.detail.blockId}"]`)
        : null;
      if (!event.detail.sectionId && event.detail.blockId && !scopedOffer) {
        return;
      }

      this.quantity = parseInt(event.detail.quantity, 10) || 1;

      if (scopedOffer) {
        this.offerDiscountType = scopedOffer.dataset.offerDiscountType || null;
        this.offerDiscountValue = parseFloat(scopedOffer.dataset.offerDiscountValue) || 0;
        this.offerDiscountScope = scopedOffer.dataset.offerDiscountScope || 'total';
      }

      this.updatePriceDisplay();
      this.dispatchSubscriptionEvent();
    }
  },

  handleOfferPricesUpdated(event) {
    const eventSectionId = event.detail?.sectionId;
    if (eventSectionId && this.sectionId && String(eventSectionId) !== String(this.sectionId)) {
      return;
    }

    const section = this.$el.closest('.shopify-section');
    if (!section) return;

    const selectedOffer = section.querySelector('.offer.is-selected');
    if (!selectedOffer) return;

    const eventBlockId = String(event.detail?.blockId || '').trim();
    const selectedBlockId = String(selectedOffer.dataset.blockId || '').trim();
    if (eventBlockId && selectedBlockId && eventBlockId !== selectedBlockId) {
      return;
    }

    this.quantity = parseInt(selectedOffer.dataset.quantity, 10) || this.quantity || 1;
    this.offerDiscountType = selectedOffer.dataset.offerDiscountType || null;
    this.offerDiscountValue = parseFloat(selectedOffer.dataset.offerDiscountValue) || 0;
    this.offerDiscountScope = selectedOffer.dataset.offerDiscountScope || 'total';
    this.updatePriceDisplay();
  },

  applyTypeSelectionSideEffects() {
    this.updateForm();
    this.dispatchSubscriptionEvent();
  },

  selectType(type) {
    this.selectedType = type;

    if (this.selectionSideEffectsTimer) {
      clearTimeout(this.selectionSideEffectsTimer);
      this.selectionSideEffectsTimer = null;
    }

    // Match offers-block responsiveness by deferring heavy side effects one tick.
    this.selectionSideEffectsTimer = setTimeout(() => {
      this.selectionSideEffectsTimer = null;
      if (this.selectedType !== type) return;
      this.applyTypeSelectionSideEffects();
    }, 0);
  },

  toggleCheckbox() {
    if (this.selectionSideEffectsTimer) {
      clearTimeout(this.selectionSideEffectsTimer);
      this.selectionSideEffectsTimer = null;
    }

    this.selectedType = this.selectedType === 'subscribe' ? 'onetime' : 'subscribe';
    this.applyTypeSelectionSideEffects();
    this.updateCheckboxPriceDisplay();
  },

  isCheckboxMode() {
    return this.$el.hasAttribute('data-checkbox-mode');
  },

  updateCheckboxPriceDisplay() {
    if (!this.isCheckboxMode()) return;

    const priceEl = this.$el.querySelector('[data-subscription-price]');
    if (priceEl) {
      priceEl.innerHTML = this.getCheckboxPriceHtml();
    }

    // Also update compare price elements (same as vertical/horizontal mode)
    const compareEls = this.$el.querySelectorAll('[data-subscription-compare]');
    if (compareEls.length > 0) {
      const compareHtml = this.getCheckboxCompareHtml();
      compareEls.forEach(el => {
        el.innerHTML = compareHtml;
      });
    }

    // Update per-unit price spans in title/subtitle
    const totals = this.getComputedPriceTotals();
    if (totals) this.updatePriceEachSpans(totals);

    // Update savings amount display for quantity-based [amount] placeholders
    this.updateSavingsAmountDisplay();
  },

  /**
   * Get compare price HTML for checkbox mode.
   */
  getCheckboxCompareHtml() {
    const totals = this.getComputedPriceTotals();
    if (!totals) return '';

    if (this.selectedType === 'onetime') {
      return this.replacePricePlaceholders(
        this.priceTemplates.onetimeCompare,
        totals.onetimePrice,
        totals.onetimeCompare,
        totals.quantity
      );
    }

    return this.replacePricePlaceholders(
      this.priceTemplates.subscribeCompare,
      totals.subscriptionPrice,
      totals.subscriptionCompare,
      totals.quantity
    );
  },

  /**
   * Get subscription price HTML for vertical/horizontal mode.
   * Used by x-html binding to reactively update when selectedSellingPlan changes.
   */
  getSubscribePriceHtml() {
    // Guard: return empty string if component not yet initialized
    // This prevents "Undefined" from showing on first load race conditions
    if (!this.priceTemplates?.subscribePrice) return '';
    const totals = this.getComputedPriceTotals();
    if (!totals) return '';

    return this.replacePricePlaceholders(
      this.priceTemplates.subscribePrice,
      totals.subscriptionPrice,
      totals.subscriptionCompare,
      totals.quantity
    );
  },

  getCheckboxPriceHtml() {
    // Guard: return empty string if component not yet initialized
    // This prevents "Undefined" from showing on first load race conditions
    if (!this.priceTemplates?.onetimePrice || !this.priceTemplates?.subscribePrice) return '';
    const totals = this.getComputedPriceTotals();
    if (!totals) return '';

    if (this.selectedType === 'onetime') {
      return this.replacePricePlaceholders(
        this.priceTemplates.onetimePrice,
        totals.onetimePrice,
        totals.onetimeCompare,
        totals.quantity
      );
    }

    return this.replacePricePlaceholders(
      this.priceTemplates.subscribePrice,
      totals.subscriptionPrice,
      totals.subscriptionCompare,
      totals.quantity
    );
  },

  selectSellingPlan(planId) {
    const numericId = parseInt(planId, 10);
    this.selectedSellingPlan = numericId;
    // Store the full plan object for direct access - avoids repeated find() lookups
    this.selectedPlanData = this.sellingPlans.find(p => Number(p.id) === numericId) || null;
    this.updateForm();
    this.updatePriceDisplay();
    this.dispatchSubscriptionEvent();
  },

  updateFormWithRetry(attempts = 0) {
    const maxAttempts = 5;
    const retryDelay = 100;

    const form = this.findProductForm();
    if (form) {
      this.updateForm();
      if (!form._subscriptionListenerAttached) {
        form._subscriptionListenerAttached = true;
        form.addEventListener('submit', () => {
          this.updateForm();
        }, { capture: true });
      }
    } else if (attempts < maxAttempts) {
      setTimeout(() => this.updateFormWithRetry(attempts + 1), retryDelay);
    }
  },

  findProductForm() {
    let form = null;

    const section = this.$el.closest('.shopify-section');
    if (section) {
      form = section.querySelector('form[data-type="add-to-cart-form"]') ||
             section.querySelector('product-form form') ||
             section.querySelector('form[action*="/cart/add"]');
    }

    if (!form) {
      form = document.querySelector('form[data-type="add-to-cart-form"]') ||
             document.querySelector('product-form form') ||
             document.querySelector('form[action*="/cart/add"]');
    }

    return form;
  },

  updateForm() {
    const form = this.findProductForm();
    if (!form) return;

    let sellingPlanInput = form.querySelector('input[name="selling_plan"]');

    if (this.selectedType === 'subscribe' && this.selectedSellingPlan) {
      if (!sellingPlanInput) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        form.appendChild(sellingPlanInput);
      }
      sellingPlanInput.value = this.selectedSellingPlan;
      sellingPlanInput.disabled = false;
    } else {
      if (sellingPlanInput) {
        sellingPlanInput.disabled = true;
        sellingPlanInput.value = '';
      }
    }
  },

  dispatchSubscriptionEvent() {
    // Use the stored plan object directly for reliable access
    const eventDetail = {
      sectionId: this.sectionId,
      type: this.selectedType,
      sellingPlan: this.selectedType === 'subscribe' ? this.selectedPlanData : null,
      sellingPlanId: this.selectedType === 'subscribe' ? this.selectedSellingPlan : null,
      sellingPlanGroupName: this.selectedType === 'subscribe' ? this.getPlanNameFromPlan() : '',
      variantPrice: this.variantPrice,
      variantComparePrice: this.variantComparePrice,
      quantity: this.quantity,
      compareBasis: this.subscribeCompareBasis
    };

    // Set state BEFORE dispatching so dependent blocks can initialize immediately.
    const state = {
      type: eventDetail.type,
      sellingPlan: eventDetail.sellingPlan,
      sellingPlanId: eventDetail.sellingPlanId,
      sellingPlanGroupName: eventDetail.sellingPlanGroupName,
      variantPrice: eventDetail.variantPrice,
      variantComparePrice: eventDetail.variantComparePrice,
      quantity: eventDetail.quantity,
      compareBasis: eventDetail.compareBasis
    };

    window.subscriptionStateBySection = window.subscriptionStateBySection || {};
    if (this.sectionId) {
      window.subscriptionStateBySection[this.sectionId] = state;
    }
    window.subscriptionState = state;

    document.dispatchEvent(new CustomEvent('subscription:type-changed', {
      detail: eventDetail
    }));
  },

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const currencyCode = window.Shopify?.currency?.active || window.__currencyCode;
    if (currencyCode) {
      const fmtOpts = { style: 'currency', currency: currencyCode, currencyDisplay: 'narrowSymbol' };
      let formatted = new Intl.NumberFormat(window.Shopify?.locale || 'en-US', fmtOpts).format(amount);
      if (window.__currencyCodeEnabled) {
        formatted += ' ' + currencyCode;
      }
      return formatted;
    }
    let fallbackFormatted = '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (window.__currencyCodeEnabled && window.__currencyCode) {
      fallbackFormatted += ' ' + window.__currencyCode;
    }
    return fallbackFormatted;
  },

  formatMoneyWithoutDecimals(cents) {
    const amount = Math.round(cents / 100);
    const currencyCode = window.Shopify?.currency?.active || window.__currencyCode;
    if (currencyCode) {
      const fmtOpts = { style: 'currency', currency: currencyCode, currencyDisplay: 'narrowSymbol', minimumFractionDigits: 0, maximumFractionDigits: 0 };
      let formatted = new Intl.NumberFormat(window.Shopify?.locale || 'en-US', fmtOpts).format(amount);
      if (window.__currencyCodeEnabled) {
        formatted += ' ' + currencyCode;
      }
      return formatted;
    }
    let fallbackFormatted = '$' + amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (window.__currencyCodeEnabled && window.__currencyCode) {
      fallbackFormatted += ' ' + window.__currencyCode;
    }
    return fallbackFormatted;
  },

  applyOfferDiscount(basePrice) {
    if (!this.offerDiscountType || this.offerDiscountValue <= 0) {
      return basePrice;
    }

    if (this.offerDiscountType === 'percentage') {
      return Math.round(basePrice * (100 - this.offerDiscountValue) / 100);
    } else if (this.offerDiscountType === 'fixed') {
      if (this.offerDiscountScope === 'per_item') {
        return Math.max(0, basePrice - this.offerDiscountValue);
      } else {
        const perItemDiscount = this.offerDiscountValue / this.quantity;
        return Math.max(0, basePrice - perItemDiscount);
      }
    }
    return basePrice;
  },

  applyOfferDiscountToLines(linePrices) {
    if (!Array.isArray(linePrices) || linePrices.length === 0) return 0;

    if (!this.offerDiscountType || this.offerDiscountValue <= 0) {
      return this.sumCents(linePrices);
    }

    if (this.offerDiscountType === 'percentage') {
      const multiplier = (100 - this.offerDiscountValue) / 100;
      return this.sumCents(linePrices.map((price) => Math.round(price * multiplier)));
    }

    if (this.offerDiscountType === 'fixed') {
      if (this.offerDiscountScope === 'per_item') {
        return this.sumCents(linePrices.map((price) => Math.max(0, price - this.offerDiscountValue)));
      }
      return Math.max(0, this.sumCents(linePrices) - this.offerDiscountValue);
    }

    return this.sumCents(linePrices);
  },

  applySubscriptionDiscount(basePrice) {
    const plan = this.selectedPlanData;
    if (!plan || !basePrice) return basePrice;

    const adjustment = plan.price_adjustments?.[0];
    if (!adjustment) return basePrice;

    if (adjustment.value_type === 'percentage') {
      return Math.round(basePrice * (100 - adjustment.value) / 100);
    } else if (adjustment.value_type === 'fixed_amount') {
      return Math.max(0, basePrice - adjustment.value);
    } else if (adjustment.value_type === 'price') {
      return Math.max(0, adjustment.value);
    }

    return basePrice;
  },

  toCents(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return 0;

      if (/^-?\d+$/.test(trimmed)) {
        return parseInt(trimmed, 10);
      }

      const normalized = trimmed.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
      const parsed = Number.parseFloat(normalized);
      if (!Number.isFinite(parsed)) return 0;

      return normalized.includes('.') ? Math.round(parsed * 100) : Math.round(parsed);
    }

    return 0;
  },

  sumCents(values) {
    return values.reduce((sum, value) => sum + this.toCents(value), 0);
  },

  getSelectedOfferElement() {
    const section = this.$el.closest('.shopify-section');
    if (!section) return null;
    return section.querySelector('.offer.is-selected');
  },

  getOffersComponentState(offer = null) {
    const section = this.$el.closest('.shopify-section');
    if (!section) return null;
    const offersRoot = offer?.closest('.offers') || section.querySelector('.offers');
    return offersRoot?._x_dataStack?.[0] || null;
  },

  getSelectedVariantIdForOfferSlot(offer, slot) {
    const offerId = String(offer?.dataset?.blockId || '').trim();
    const stateVariantId = String(this.getOffersComponentState(offer)?.selectedVariants?.[offerId]?.[slot] || '').trim();
    if (stateVariantId) return stateVariantId;

    const container = offer?.querySelector(`.offer__variants-slot[data-slot="${slot}"]`);
    const dropdownVariantId = String(container?.querySelector('.offer__variant-select')?.value || '').trim();
    if (dropdownVariantId) return dropdownVariantId;

    const pillVariantId = String(container?.querySelector('.offer__variant-pill.is-active')?.dataset.variantId || '').trim();
    if (pillVariantId) return pillVariantId;

    return '';
  },

  getVariantPricesForOfferSlot(offer, slot, variantId, fallbackBase, fallbackCompare) {
    const container = offer?.querySelector(`.offer__variants-slot[data-slot="${slot}"]`);
    const variantSelect = container?.querySelector('.offer__variant-select');
    if (!variantSelect) {
      const comparePrice = fallbackCompare > 0 ? fallbackCompare : fallbackBase;
      return { basePrice: fallbackBase, comparePrice };
    }

    const option = Array.from(variantSelect.options || []).find((opt) => String(opt.value) === String(variantId));
    const resolvedOption = option || variantSelect.selectedOptions?.[0] || null;

    const basePrice = this.toCents(resolvedOption?.dataset?.price);
    const compareRaw = this.toCents(resolvedOption?.dataset?.comparePrice);
    const normalizedBase = basePrice > 0 ? basePrice : fallbackBase;
    const normalizedCompare = compareRaw > 0 ? compareRaw : (normalizedBase > 0 ? normalizedBase : fallbackCompare);

    return {
      basePrice: Math.max(0, normalizedBase),
      comparePrice: Math.max(0, normalizedCompare)
    };
  },

  getSelectedOfferLinePrices() {
    const selectedOffer = this.getSelectedOfferElement();
    if (!selectedOffer) return null;

    const quantity = Math.max(parseInt(selectedOffer.dataset.quantity, 10) || this.quantity || 1, 1);
    const fallbackBase = Math.max(0, this.toCents(selectedOffer.dataset.basePrice) || this.variantPrice || 0);
    const fallbackCompareTotal = this.toCents(selectedOffer.dataset.comparePrice);
    let fallbackCompare = fallbackCompareTotal > 0 ? Math.round(fallbackCompareTotal / quantity) : 0;
    if (fallbackCompare <= 0) {
      fallbackCompare = this.variantComparePrice > 0 ? this.variantComparePrice : fallbackBase;
    }

    const baseLinePrices = Array(quantity).fill(fallbackBase);
    const compareLinePrices = Array(quantity).fill(fallbackCompare > 0 ? fallbackCompare : fallbackBase);

    for (let slot = 1; slot <= quantity; slot++) {
      const variantId = this.getSelectedVariantIdForOfferSlot(selectedOffer, slot);
      const { basePrice, comparePrice } = this.getVariantPricesForOfferSlot(
        selectedOffer,
        slot,
        variantId,
        fallbackBase,
        fallbackCompare
      );

      baseLinePrices[slot - 1] = Math.max(0, basePrice);
      compareLinePrices[slot - 1] = Math.max(0, comparePrice > 0 ? comparePrice : basePrice);
    }

    return { quantity, baseLinePrices, compareLinePrices };
  },

  getComputedPriceTotals() {
    const offerLinePrices = this.getSelectedOfferLinePrices();

    if (offerLinePrices) {
      const { quantity, baseLinePrices, compareLinePrices } = offerLinePrices;
      const subscriptionLinePrices = baseLinePrices.map((price) => this.applySubscriptionDiscount(price));
      const compareLineTotal = this.sumCents(compareLinePrices);
      const onetimeBaseTotal = this.sumCents(baseLinePrices);

      const onetimePrice = this.applyOfferDiscountToLines(baseLinePrices);
      const onetimeCompare = compareLineTotal > 0 ? compareLineTotal : onetimeBaseTotal;
      const subscriptionPrice = this.applyOfferDiscountToLines(subscriptionLinePrices);

      let subscriptionCompare;
      if (this.subscribeCompareBasis === 'maximum' || this.subscribeCompareBasis === 'compare_at') {
        subscriptionCompare = compareLineTotal > 0 ? compareLineTotal : onetimeCompare;
      } else if (this.subscribeCompareBasis === 'before_discounts') {
        subscriptionCompare = onetimeBaseTotal;
      } else {
        subscriptionCompare = this.applyOfferDiscountToLines(baseLinePrices);
      }

      return {
        quantity,
        onetimePrice: Math.max(0, onetimePrice),
        onetimeCompare: Math.max(0, onetimeCompare),
        subscriptionPrice: Math.max(0, subscriptionPrice),
        subscriptionCompare: Math.max(0, subscriptionCompare)
      };
    }

    const quantity = Math.max(this.quantity || 1, 1);
    const onetimeLinePrices = Array(quantity).fill(Math.max(0, this.variantPrice || 0));
    const subscriptionLinePrices = onetimeLinePrices.map((price) => this.applySubscriptionDiscount(price));

    const onetimePrice = this.applyOfferDiscountToLines(onetimeLinePrices);
    const onetimeCompare = this.sumCents(onetimeLinePrices);
    const subscriptionPrice = this.applyOfferDiscountToLines(subscriptionLinePrices);
    const subscriptionCompareSource = this.getSubscriptionCompareSource();
    const subscriptionCompare = this.shouldApplyOfferDiscountToCompare()
      ? this.applyOfferDiscountToLines(Array(quantity).fill(Math.max(0, subscriptionCompareSource)))
      : Math.max(0, subscriptionCompareSource * quantity);

    return {
      quantity,
      onetimePrice: Math.max(0, onetimePrice),
      onetimeCompare: Math.max(0, onetimeCompare),
      subscriptionPrice: Math.max(0, subscriptionPrice),
      subscriptionCompare: Math.max(0, subscriptionCompare)
    };
  },

  /**
   * Get the subscription compare source price based on the setting.
   * 'onetime' = variant.price (offer discount will be applied)
   * 'before_discounts' = variant.price (no offer discount)
   * 'maximum' = variant.compare_at_price (no offer discount, MSRP)
   */
  getSubscriptionCompareSource() {
    // 'maximum' or legacy 'compare_at' both use variant.compare_at_price (MSRP)
    if ((this.subscribeCompareBasis === 'maximum' || this.subscribeCompareBasis === 'compare_at') && this.variantComparePrice > 0) {
      return this.variantComparePrice;
    }
    return this.variantPrice;
  },

  /**
   * Check if offer discount should be applied to the compare price.
   * Only 'onetime' mode applies the offer discount to compare.
   */
  shouldApplyOfferDiscountToCompare() {
    // Only 'onetime' mode applies offer discount to compare
    // Legacy 'compare_at' and 'before_discounts' and 'maximum' do NOT apply offer discount
    return this.subscribeCompareBasis === 'onetime';
  },

  updatePriceDisplay() {
    // In checkbox mode, delegate to the checkbox-specific method
    if (this.isCheckboxMode()) {
      this.updateCheckboxPriceDisplay();
      return;
    }

    const totals = this.getComputedPriceTotals();
    if (!totals) return;

    this.quantity = totals.quantity;
    this.updateOnetimePriceElements(totals.onetimePrice, totals.onetimeCompare, totals.quantity);
    this.updateSubscriptionPriceElements(totals.subscriptionPrice, totals.subscriptionCompare, totals.quantity);
    this.updatePriceEachSpans(totals);
    this.updateSavingsAmountDisplay();
  },

  updateOnetimePriceElements(totalPrice, totalCompare, quantity = this.quantity) {
    const onetimePriceEl = this.$el.querySelector('[data-onetime-price]');
    if (onetimePriceEl) {
      onetimePriceEl.innerHTML = this.replacePricePlaceholders(this.priceTemplates.onetimePrice, totalPrice, totalCompare, quantity);
    }

    const onetimeCompareEls = this.$el.querySelectorAll('[data-onetime-compare]');
    onetimeCompareEls.forEach(el => {
      if (totalCompare > 0 && totalCompare > totalPrice) {
        el.innerHTML = this.replacePricePlaceholders(this.priceTemplates.onetimeCompare, totalPrice, totalCompare, quantity);
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  },

  updateSubscriptionPriceElements(totalPrice, totalCompare, quantity = this.quantity) {
    const subscriptionPriceEl = this.$el.querySelector('[data-subscription-price]');
    if (subscriptionPriceEl) {
      subscriptionPriceEl.innerHTML = this.replacePricePlaceholders(this.priceTemplates.subscribePrice, totalPrice, totalCompare, quantity);
    }

    const subscriptionCompareEls = this.$el.querySelectorAll('[data-subscription-compare]');
    subscriptionCompareEls.forEach(el => {
      el.innerHTML = this.replacePricePlaceholders(this.priceTemplates.subscribeCompare, totalPrice, totalCompare, quantity);
    });
  },

  /**
   * Update [price_each] and [compare_each] placeholder spans in title/subtitle.
   * These are data-attribute spans rendered by Liquid that need JS updates
   * when quantity or subscription state changes.
   */
  updatePriceEachSpans(totals) {
    if (!totals) return;
    const q = Math.max(totals.quantity || 1, 1);

    const subPriceEach = q > 1 ? Math.round(totals.subscriptionPrice / q) : totals.subscriptionPrice;
    const subCompareEach = q > 1 ? Math.round(totals.subscriptionCompare / q) : totals.subscriptionCompare;
    const otPriceEach = q > 1 ? Math.round(totals.onetimePrice / q) : totals.onetimePrice;
    const otCompareEach = q > 1 ? Math.round(totals.onetimeCompare / q) : totals.onetimeCompare;

    this.$el.querySelectorAll('[data-subscription-price-each]').forEach(el => {
      el.textContent = this.formatMoney(subPriceEach);
    });
    this.$el.querySelectorAll('[data-subscription-compare-each]').forEach(el => {
      el.textContent = this.formatMoney(subCompareEach);
    });
    this.$el.querySelectorAll('[data-onetime-price-each]').forEach(el => {
      el.textContent = this.formatMoney(otPriceEach);
    });
    this.$el.querySelectorAll('[data-onetime-compare-each]').forEach(el => {
      el.textContent = this.formatMoney(otCompareEach);
    });
    this.$el.querySelectorAll('[data-subscription-quantity]').forEach(el => {
      el.textContent = q;
    });
  },

  /**
   * Update [amount] and [amount_rounded] placeholder displays based on quantity.
   * These show total savings (per-unit savings * quantity).
   */
  updateSavingsAmountDisplay() {
    // Recalculate per-unit savings based on current selling plan
    const currentPerUnitSavings = this.calculatePerUnitSavings();
    const currentPerUnitSavingsRounded = Math.ceil(currentPerUnitSavings / 100) * 100;

    // Calculate total savings based on quantity
    const totalSavings = currentPerUnitSavings * this.quantity;
    const totalSavingsRounded = currentPerUnitSavingsRounded * this.quantity;

    // Update exact amount spans
    const exactAmountEls = this.$el.querySelectorAll('[data-subscription-amount="exact"]');
    exactAmountEls.forEach(el => {
      el.textContent = this.formatMoney(totalSavings);
    });

    // Update rounded amount spans (whole dollars, no decimals)
    const roundedAmountEls = this.$el.querySelectorAll('[data-subscription-amount="rounded"]');
    roundedAmountEls.forEach(el => {
      el.textContent = this.formatMoneyWithoutDecimals(totalSavingsRounded);
    });

    // Update [sub_discount] spans when selling plan changes
    this.updateSubDiscountDisplay();
  },

  /**
   * Update [sub_discount] and [sub_discount_rounded] placeholder displays based on current selling plan.
   */
  updateSubDiscountDisplay() {
    const discountText = this.getSubscriptionDiscountText();
    const discountEls = this.$el.querySelectorAll('[data-subscription-discount]');
    discountEls.forEach(el => {
      el.textContent = discountText;
    });

    const discountTextRounded = this.getSubscriptionDiscountTextRounded();
    const discountRoundedEls = this.$el.querySelectorAll('[data-subscription-discount-rounded]');
    discountRoundedEls.forEach(el => {
      el.textContent = discountTextRounded;
    });
  },

  /**
   * Update [number], [unit], and [plan_name] placeholder displays based on current selling plan.
   */
  updateFrequencyDisplay() {
    const { number, unit } = this.getFrequencyFromPlan();
    const planName = this.getPlanNameFromPlan();

    const numberEls = this.$el.querySelectorAll('[data-subscription-frequency-number]');
    numberEls.forEach(el => {
      el.textContent = number;
    });

    const unitEls = this.$el.querySelectorAll('[data-subscription-frequency-unit]');
    unitEls.forEach(el => {
      el.textContent = unit;
    });

    const planNameEls = this.$el.querySelectorAll('[data-subscription-plan-name]');
    planNameEls.forEach(el => {
      el.textContent = planName;
    });
  },

  /**
   * Parse [number] and [unit] from the current selling plan's options.
   * Returns { number: '1', unit: '' } as defaults if not found.
   */
  getFrequencyFromPlan() {
    const plan = this.selectedPlanData;
    let number = '1';
    let unit = '';

    if (!plan || !plan.options || plan.options.length === 0) {
      return { number, unit };
    }

    // Parse each option's value to find number and time unit
    for (const option of plan.options) {
      if (!option.value) continue;

      const parts = option.value.split(' ');
      for (const part of parts) {
        const partLower = part.toLowerCase();
        // Check for time units
        if (['month', 'months', 'week', 'weeks', 'day', 'days', 'year', 'years'].includes(partLower)) {
          unit = part;
        } else {
          // Check if it's a number
          const asNumber = parseInt(part, 10);
          if (!isNaN(asNumber) && asNumber.toString() === part && part !== '0') {
            number = part;
          }
        }
      }
    }

    return { number, unit };
  },

  getPlanNameFromPlan() {
    const plan = this.selectedPlanData;
    const groupNameFromPlan = plan?.group_name || plan?.groupName || '';
    if (groupNameFromPlan) return String(groupNameFromPlan);

    const groupNameFromContainer = this.$el?.dataset?.subscriptionGroupName || '';
    if (groupNameFromContainer) return String(groupNameFromContainer);

    if (plan?.name) return String(plan.name);
    return '';
  },

  /**
   * Calculate per-unit savings based on current selling plan and compare basis.
   */
  calculatePerUnitSavings() {
    const totals = this.getComputedPriceTotals();
    if (!totals) return 0;

    const quantity = Math.max(totals.quantity || this.quantity || 1, 1);
    const totalSavings = totals.subscriptionCompare - totals.subscriptionPrice;
    if (totalSavings > 0) {
      return Math.round(totalSavings / quantity);
    }

    return 0;
  },

  /**
   * Get subscription discount text based on the current selling plan.
   * Returns "X%" for percentage, "$X.XX" for fixed_amount, or the price for fixed price.
   */
  getSubscriptionDiscountText() {
    const plan = this.selectedPlanData;
    if (!plan) return '';

    const adjustment = plan.price_adjustments?.[0];
    if (!adjustment) return '';

    if (adjustment.value_type === 'percentage' && adjustment.value > 0) {
      return adjustment.value + '%';
    } else if (adjustment.value_type === 'fixed_amount' && adjustment.value > 0) {
      return this.formatMoney(adjustment.value);
    } else if (adjustment.value_type === 'price') {
      return this.formatMoney(adjustment.value);
    }

    return '';
  },

  /**
   * Get subscription discount text (rounded) based on the current selling plan.
   * Returns "X%" for percentage, or rounded dollar amount for fixed_amount/price.
   */
  getSubscriptionDiscountTextRounded() {
    const plan = this.selectedPlanData;
    if (!plan) return '';

    const adjustment = plan.price_adjustments?.[0];
    if (!adjustment) return '';

    if (adjustment.value_type === 'percentage' && adjustment.value > 0) {
      return adjustment.value + '%';
    } else if (adjustment.value_type === 'fixed_amount' && adjustment.value > 0) {
      const rounded = Math.ceil(adjustment.value / 100) * 100;
      return this.formatMoneyWithoutDecimals(rounded);
    } else if (adjustment.value_type === 'price') {
      const rounded = Math.ceil(adjustment.value / 100) * 100;
      return this.formatMoneyWithoutDecimals(rounded);
    }

    return '';
  },

  /**
   * Replace dynamic placeholders in price templates.
   */
  replacePricePlaceholders(template, price, comparePrice, quantity = this.quantity) {
    const safeQuantity = Math.max(quantity || 1, 1);
    // Calculate per-unit prices by dividing total by quantity
    const priceEach = safeQuantity > 1 ? Math.round(price / safeQuantity) : price;
    const compareEach = safeQuantity > 1 ? Math.round(comparePrice / safeQuantity) : comparePrice;
    const { number, unit } = this.getFrequencyFromPlan();
    const planName = this.getPlanNameFromPlan();

    return template
      .replace(/\[price\]/g, this.formatMoney(price))
      .replace(/\[price_per_(\d+)\]/g, (m, n) => { const d = parseInt(n, 10); return d > 0 ? this.formatMoney(Math.round(price / d)) : m; })
      .replace(/\[compare_price\]/g, this.formatMoney(comparePrice))
      .replace(/\[price_each\]/g, this.formatMoney(priceEach))
      .replace(/\[compare_each\]/g, this.formatMoney(compareEach))
      .replace(/\[sub_discount\]/g, this.getSubscriptionDiscountText())
      .replace(/\[sub_discount_rounded\]/g, this.getSubscriptionDiscountTextRounded())
      .replace(/\[number\]/g, number)
      .replace(/\[unit\]/g, unit)
      .replace(/\[quantity\]/g, safeQuantity)
      .replace(/\[br\]/g, '<br>')
      .replace(/\[plan_name\]/g, planName);
  },

  getSubscriptionPrice() {
    return this.applySubscriptionDiscount(this.variantPrice);
  },

});

// Keep a global factory for x-data="subscriptionsBlock()" so Alpine expression
// evaluation never depends on Alpine.data registration timing.
window.subscriptionsBlock = function subscriptionsBlock() {
  return subscriptionsBlockComponent();
};

// Register with Alpine.js
function registerSubscriptionsBlock() {
  if (!window.Alpine) return;
  Alpine.data('subscriptionsBlock', subscriptionsBlockComponent);
}

function initializeSubscriptionsElements() {
  document.querySelectorAll('[x-data*="subscriptionsBlock"]').forEach(el => {
    // Match offers-block behavior: only initialize missing Alpine trees.
    if (el._x_dataStack && el._x_dataStack.length > 0) return;
    Alpine.initTree(el);
  });
}

function attachSubscriptionsFallbackHandler() {
  if (window.__subscriptionsBlockFallbackAttached) return;
  window.__subscriptionsBlockFallbackAttached = true;

  document.addEventListener('click', (event) => {
    const option = event.target.closest('.subscription-option');
    if (!option || !window.Alpine) return;

    const root = option.closest('[x-data*="subscriptionsBlock"]');
    if (!root) return;

    const existingComponent = root._x_dataStack?.[0];
    if (existingComponent && typeof existingComponent.selectType === 'function') return;

    try {
      Alpine.initTree(root);
    } catch (e) {
      return;
    }

    const component = root._x_dataStack?.[0];
    if (!component || typeof component.selectType !== 'function') return;

    const type = option.classList.contains('subscription-option--onetime') ? 'onetime' : 'subscribe';
    component.selectType(type);
  }, true);
}

function bootstrapSubscriptionsBlock() {
  registerSubscriptionsBlock();
  initializeSubscriptionsElements();
  attachSubscriptionsFallbackHandler();
}

if (!window.__subscriptionsBlockBootstrapped) {
  window.__subscriptionsBlockBootstrapped = true;

  if (window.Alpine) {
    bootstrapSubscriptionsBlock();
  }

  document.addEventListener('alpine:init', bootstrapSubscriptionsBlock);

  // Re-initialize after Shopify theme editor re-renders the section (e.g. style change)
  document.addEventListener('shopify:section:load', bootstrapSubscriptionsBlock);
}
